<template>
  <div class="level-list">
    <el-card>
      <div slot="header" class="header">
        <span>关卡配置</span>
        <el-button type="primary" size="small" @click="handleAdd">新增关卡</el-button>
      </div>
      <el-table :data="tableData" style="width: 100%" v-loading="loading">
        <el-table-column prop="level_num" label="关卡号" width="80" sortable></el-table-column>
        <el-table-column prop="name" label="名称"></el-table-column>
        <el-table-column prop="question_count" label="题目数" width="100"></el-table-column>
        <el-table-column prop="time_limit" label="限时(秒)" width="100"></el-table-column>
        <el-table-column prop="pass_rate" label="通过率" width="100">
          <template slot-scope="scope">{{ scope.row.pass_rate * 100 }}%</template>
        </el-table-column>
        <el-table-column prop="difficulty" label="难度" width="100">
          <template slot-scope="scope">
            <el-tag :type="getDifficultyType(scope.row.difficulty)">{{ scope.row.difficulty }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template slot-scope="scope">
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible">
      <el-form :model="form" label-width="100px">
        <el-form-item label="关卡号">
          <el-input-number v-model="form.level_num" :disabled="isEdit"></el-input-number>
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="form.name"></el-input>
        </el-form-item>
        <el-form-item label="题目数量">
          <el-input-number v-model="form.question_count" :min="1"></el-input-number>
        </el-form-item>
        <el-form-item label="限时(秒)">
          <el-input-number v-model="form.time_limit" :min="10"></el-input-number>
        </el-form-item>
        <el-form-item label="通过率">
          <el-slider v-model="form.pass_rate" :min="0" :max="1" :step="0.05" show-input :format-tooltip="val => val*100+'%'"></el-slider>
        </el-form-item>
        <el-form-item label="难度">
          <el-select v-model="form.difficulty">
            <el-option label="简单" value="easy"></el-option>
            <el-option label="中等" value="medium"></el-option>
            <el-option label="困难" value="hard"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="题型配置">
          <div class="type-toolbar">
            <div class="type-summary">已选 {{ selectedTypes.length }} 项</div>
            <div>
              <el-button size="mini" @click="selectAllTypes">全选</el-button>
              <el-button size="mini" @click="clearAllTypes">清空</el-button>
            </div>
          </div>
          <el-checkbox-group v-model="selectedTypes">
            <el-collapse v-model="activeTypeGroups">
              <el-collapse-item v-for="group in typeGroups" :key="group.key" :name="group.key">
                <template slot="title">
                  <span>{{ group.title }}</span>
                  <span class="type-group-count">{{ group.options.length }}项</span>
                </template>
                <div class="type-group">
                  <el-checkbox v-for="opt in group.options" :key="opt.value" :label="opt.value">
                    {{ opt.label }}
                  </el-checkbox>
                </div>
              </el-collapse-item>
            </el-collapse>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.is_active" active-text="启用" inactive-text="禁用"></el-switch>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveLevel">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api';

const TYPE_GROUPS = [
  {
    key: 'decimal',
    title: '小数运算',
    options: [
      { value: 'decimal_add', label: '小数加法' },
      { value: 'decimal_sub', label: '小数减法' },
      { value: 'decimal_mul', label: '小数乘法' },
      { value: 'decimal_div', label: '小数除法' },
      { value: 'rounding', label: '四舍五入' }
    ]
  },
  {
    key: 'factor',
    title: '因数倍数',
    options: [
      { value: 'gcd', label: '最大公因数' },
      { value: 'lcm', label: '最小公倍数' }
    ]
  },
  {
    key: 'fraction',
    title: '分数专题',
    options: [
      { value: 'fraction_add', label: '分数加法' },
      { value: 'fraction_sub', label: '分数减法' },
      { value: 'fraction_compare', label: '分数比大小' },
      { value: 'fraction_simplify', label: '分数约分' },
      { value: 'fraction_decimal_convert', label: '分数小数互化' },
      { value: 'fraction_mixed', label: '分数混合运算' },
      { value: 'fraction_shortcut', label: '分数简便计算' }
    ]
  },
  {
    key: 'compare',
    title: '比较专题',
    options: [{ value: 'comparison', label: '比大小' }]
  },
  {
    key: 'geometry',
    title: '几何专题',
    options: [
      { value: 'area', label: '图形面积' },
      { value: 'surface_area', label: '表面积' },
      { value: 'volume', label: '体积' },
      { value: 'volume_unit_convert', label: '容积换算' }
    ]
  }
];

export default {
  data() {
    return {
      tableData: [],
      loading: false,
      dialogVisible: false,
      isEdit: false,
      form: {
        level_num: 1,
        name: '',
        question_count: 20,
        time_limit: 600,
        pass_rate: 0.8,
        difficulty: 'easy',
        question_types: '',
        is_active: true
      },
      selectedTypes: [],
      typeGroups: TYPE_GROUPS,
      activeTypeGroups: TYPE_GROUPS.map(g => g.key)
    };
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑关卡' : '新增关卡';
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        this.tableData = await api.getLevels();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    getDifficultyType(diff) {
      const map = { easy: 'success', medium: 'warning', hard: 'danger' };
      return map[diff] || 'info';
    },
    handleAdd() {
      this.isEdit = false;
      this.form = {
        level_num: this.tableData.length + 1,
        name: `第${this.tableData.length + 1}关`,
        question_count: 20,
        time_limit: 600,
        pass_rate: 0.8,
        difficulty: 'easy',
        question_types: 'decimal_add',
        is_active: true
      };
      this.selectedTypes = ['decimal_add'];
      this.activeTypeGroups = this.typeGroups.map(g => g.key);
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.form = { ...row };
      this.selectedTypes = row.question_types ? row.question_types.split(',') : [];
      this.activeTypeGroups = this.typeGroups.map(g => g.key);
      this.dialogVisible = true;
    },
    selectAllTypes() {
      const all = [];
      this.typeGroups.forEach(g => {
        g.options.forEach(opt => {
          all.push(opt.value);
        });
      });
      this.selectedTypes = all;
    },
    clearAllTypes() {
      this.selectedTypes = [];
    },
    async saveLevel() {
      this.form.question_types = this.selectedTypes.join(',');
      try {
        if (this.isEdit) {
          await api.updateLevel(this.form.id, this.form);
          this.$message.success('更新成功');
        } else {
          await api.createLevel(this.form);
          this.$message.success('创建成功');
        }
        this.dialogVisible = false;
        this.fetchData();
      } catch (e) {
        console.error(e);
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确认删除该关卡?', '提示', { type: 'warning' });
        await api.deleteLevel(row.id);
        this.$message.success('删除成功');
        this.fetchData();
      } catch (e) {
        // Cancelled or error
      }
    }
  }
};
</script>

<style scoped>
.level-list {
  padding: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.type-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.type-summary {
  color: #606266;
  font-size: 12px;
}
.type-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  padding: 6px 0 10px;
}
.type-group-count {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}
</style>
